import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth";
import type { Role } from "@/types/api";

/**
 * Route guard, adapted from PayrollSystem's ProtectedRoute.tsx (drops the
 * tenant-membership dimension — role lives directly on the user here).
 *
 * 1. Redirects unauthenticated users to /login with a `from` query param
 *    so they can be sent back after login.
 * 2. If `roles` is supplied, also gates by role — users whose role isn't
 *    in the list get bounced to their default landing page. The backend
 *    already gates the relevant API endpoints (IsAdmin/IsStaff), so this
 *    is UX polish — it stops the confusing "page renders, every request
 *    403s" experience for a Staff user who bookmarks an admin URL.
 *
 * Until the auth store has hydrated from localStorage on app boot, render
 * nothing — otherwise we'd flash a redirect for users who actually have
 * a valid token.
 */
export function ProtectedRoute({
  children,
  roles,
}: {
  children: ReactNode;
  roles?: Role[];
}) {
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const location = useLocation();

  if (!isHydrated) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (roles && roles.length > 0 && (!role || !roles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
